import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "../utils/index";

/*
 * Figma (1774:1994), not upstream: 12px padding and corners, the icon in a
 * 32px chip tinted with the state colour, title and description in
 * --foreground (the description at 80%). Two appearances: `card` sits on
 * --background with a --border edge; `callout` takes a 10% wash of the state
 * colour with an edge in it. A `size-*` class on the icon replaces the 32px
 * chip size, as upstream allows. `info` is ours: Figma has no info state, so it
 * follows the others with --info. `text-start` rather than upstream's
 * `text-left`, so right-to-left pages align to the right.
 */
const alertVariants = cva(
  "group/alert relative grid w-full gap-0.5 rounded-xl border p-3 text-start text-sm text-foreground has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pe-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-3 *:[svg]:row-span-2 *:[svg:not([class*='size-'])]:size-8 *:[svg]:rounded-lg *:[svg]:p-2",
  {
    variants: {
      variant: {
        default: "*:[svg]:bg-primary/20 *:[svg]:text-primary",
        destructive: "*:[svg]:bg-destructive/20 *:[svg]:text-destructive",
        warning: "*:[svg]:bg-warning/20 *:[svg]:text-warning",
        info: "*:[svg]:bg-info/20 *:[svg]:text-info",
      },
      appearance: {
        card: "border-border bg-background",
        callout: "",
      },
    },
    compoundVariants: [
      {
        appearance: "callout",
        variant: "default",
        className: "border-primary bg-primary/10",
      },
      {
        appearance: "callout",
        variant: "destructive",
        className: "border-destructive bg-destructive/10",
      },
      {
        appearance: "callout",
        variant: "warning",
        className: "border-warning bg-warning/10",
      },
      {
        appearance: "callout",
        variant: "info",
        className: "border-info bg-info/10",
      },
    ],
    defaultVariants: {
      variant: "default",
      appearance: "card",
    },
  },
);

function Alert({
  className,
  variant,
  appearance,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant, appearance }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "font-semibold group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-sm text-balance text-foreground/80 md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4",
        className,
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("absolute top-2 rtl:left-2 ltr:right-2", className)}
      {...props}
    />
  );
}

export { Alert, AlertAction, AlertDescription, AlertTitle };
